import * as React from 'react';
import { Fragment } from 'react';
import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { Modifier, OptionsGeneric } from '@popperjs/core';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import { propStyles, style } from '../utils/propStyles';
import { verticalAlign } from '../utils/customPropStyles';
import Icon from './Icon';
import ClickLayer from './ClickLayer';
import { AccessibilityStore, EntityStore, TranslationStore } from '../stores';
import { Popper } from '../utils/Popper';
import { AnalyticsContext } from '../models/analyticsContext';
import { Theme } from '../models/Theme';
import { translate } from './Translate';
import { KEYBOARD_KEYS } from 'src/constants';
import { KeyboardEvents } from '../utils/accessibility/KeyboardEvents';

const arrowFill = style({
    prop: 'bgColor',
    key: 'color',
    cssProperty: 'fill',
});

const arrowStroke = style({
    prop: 'borderColor',
    key: 'color',
    cssProperty: 'stroke',
});

const ArrowIcon = () => (
    <svg width="24px" height="24px" viewBox="0 0 24 24">
        <path className="arrow__path" d="M7,10L12,15L17,10H7Z" />
    </svg>
);

const Arrow = styled<any, any>('div', 'arrow', 'tooltip')`
    position: absolute;
    height: 24px;
    width: 24px;
    .arrow__path {
        fill: inherit;
        stroke: inherit;
        stroke-width: 1px;
        stroke-dasharray: 14, 7, 0;
    }
`;

const TooltipWrapper = styled<any, any>('div', 'tooltipWrapper', 'tooltip')`
    ${propStyles};
    ${verticalAlign};
`;

TooltipWrapper.defaultProps = {
    display: 'inline-block',
};

const TooltipIconButton = styled<any, any>(
    'button',
    'tooltipIconButton',
    'tooltip'
)`
    ${propStyles};
    outline: none;
`;

TooltipIconButton.defaultProps = {
    display: 'inline',
    color: 'primary',
    bgColor: 'transparent',
    borderStyle: 'none',
    cursor: 'pointer',
};

const TooltipIcon = styled<any, any>(Icon, 'tooltipIcon', 'tooltip')``;

TooltipIcon.defaultProps = {
    height: '16px',
    width: '16px',
};

const TooltipCharacter = styled<any, any>('div', 'tooltipCharacter', 'tooltip')`
    ${propStyles};
`;

TooltipCharacter.defaultProps = {
    fontSize: '9px',
    minWidth: '1.5em',
    margin: '0 0.5em',
    padding: '0.15em 0.25em',
    color: 'white',
    bgColor: 'primary',
    borderRadius: '1.5em',
    textAlign: 'center',
};

const TooltipContent = styled<any, any>('div', 'tooltipContent', 'tooltip')`
    ${propStyles};
    cursor: pointer;

    ${Arrow} {
        ${arrowFill};
        ${arrowStroke};
        transform-origin: center;
    }

    &[data-popper-placement='top'] ${Arrow} {
        bottom: -14px;
    }

    &[data-popper-placement='bottom'] ${Arrow} {
        top: -14px;
        transform: rotate(180deg);
    }

    &[data-popper-placement='left'] ${Arrow} {
        right: -14px;
        transform: rotate(-90deg);
    }

    &[data-popper-placement='right'] ${Arrow} {
        left: -14px;
        transform: rotate(90deg);
    }
`;

TooltipContent.defaultProps = {
    minWidth: '200px',
    maxWidth: '300px',
    color: 'body',
    bgColor: 'white',
    fontSize: 'xs',
    fontFamily: 'body',
    fontWeight: 'regular',
    textAlign: 'center',
    padding: 'sm',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'gray500',
    borderRadius: 'sm',
    boxShadow: 'sm',
    zIndex: 'popover',
};

export interface AccessibilityContext {
    key?: string;
    placeholders?: string;
}

interface TooltipProps {
    theme?: Theme;
    tooltipCharacter?: string;
    iconFilename?: string;
    showContent?: boolean;
    content: Array<string>;
    verticalAlign?: string;
    accessibilityContext?: AccessibilityContext;
    entityStore?: EntityStore;
    translationStore?: TranslationStore;
    accessibilityStore?: AccessibilityStore;
    analyticsContext?: AnalyticsContext;
    rowAccessibleContext?: string;
    labelFormatter?: () => void;
    formatter?: () => void;
    rowText?: string;
}

@inject('accessibilityStore', 'dataStore', 'entityStore', 'translationStore')
@observer
class Tooltip extends Popper<TooltipProps> {
    popper;
    tooltipNode;
    tooltipContentNode;
    arrowNode;
    @observable show = false;
    keyboardEvents: KeyboardEvents<HTMLButtonElement>;

    popperOptions: Partial<OptionsGeneric<Partial<Modifier<any, any>>>> = {
        placement: 'top',
        modifiers: [
            {
                name: 'flip',
                enabled: false,
            },
            {
                name: 'offset',
                options: {
                    offset: [0, 5],
                },
            },
        ],
    };

    private popupId: string;

    constructor(props: TooltipProps) {
        super(props);
        this.initializeKeyboardEvents();

        const suffix =
            (props.analyticsContext?.metricContext
                ? `-${props.analyticsContext.metricContext}`
                : '') +
            `-${Math.random()
                .toString(36)
                .slice(2)}`;
        this.popupId = `tooltip-content${suffix}`;
    }

    get popperEnabled() {
        return true;
    }

    toggleTooltip = e => {
        const {
            dataStore,
            entityStore,
            content,
            analyticsContext,
        } = this.props;

        e.stopPropagation();
        e.preventDefault();
        this.show = !this.show;
        this.updatePopper();

        if (this.show) {
            dataStore.logDataLayer('tooltip_opened', {
                relatedEntityId: entityStore?.value?.clientCode || 'Global',
                sourceContext: content,
                source: analyticsContext?.source,
                tooltipCode: analyticsContext?.tooltipCode || 'information',
            });
        }
    };

    closeTooltip = e => {
        e.stopPropagation();
        e.preventDefault();
        this.show = false;
        this.updatePopper();
    };

    initializeKeyboardEvents() {
        this.keyboardEvents = new KeyboardEvents({
            name: 'WatchListToggle',
            debug: false,
            defaults: {
                accept: {
                    down: {
                        preventDefault: true,
                        stopPropagation: true,
                    },
                },
            },
        });

        this.keyboardEvents.bindEventToMany(
            [
                { type: 'key', direction: 'down', key: KEYBOARD_KEYS.ENTER },
                { type: 'key', direction: 'down', key: KEYBOARD_KEYS.SPACE },
                { type: 'key', direction: 'down', key: KEYBOARD_KEYS.SPACEBAR },
            ],
            event => {
                this.toggleTooltip(event);
            }
        );

        this.keyboardEvents.bindKeyEvent(
            KEYBOARD_KEYS.ESCAPE,
            'down',
            event => {
                this.closeTooltip(event);
            }
        );
    }

    stopPropagation = e => {
        e.stopPropagation();
    };

    @computed
    get content() {
        return this.props.content || [];
    }

    @computed
    get hasContent() {
        return this.content.length > 0;
    }

    @computed
    get tooltipContent() {
        const { theme, analyticsContext } = this.props;
        const { metricContext } = analyticsContext;
        const { content } = this;

        // Do not use display: 'none' here as it causes the popper to fail when trying to work out the init position
        const hiddenStyle: React.CSSProperties = {
            visibility: 'hidden',
            pointerEvents: 'none',
        };

        return (
            <TooltipContent
                id={this.popupId}
                role="tooltip"
                ref={this.setPopupNodeRef}
                onClick={this.toggleTooltip}
                {...theme.TooltipContent}
                style={(!this.show && hiddenStyle) || {}}
                data-metric-id={`tooltip-content-${metricContext}`}
            >
                {content.map((c, index) => (
                    <div
                        aria-live="polite"
                        role="status"
                        onClick={this.stopPropagation}
                        key={index}
                        dangerouslySetInnerHTML={{
                            __html: c,
                        }}
                    />
                ))}
                <Arrow
                    ref={this.setArrowNodeRef}
                    aria-hidden={true}
                    className="popper__arrow"
                    x-arrow=""
                >
                    <ArrowIcon />
                </Arrow>
            </TooltipContent>
        );
    }

    render() {
        const {
            theme,
            tooltipCharacter,
            showContent = true,
            accessibilityContext,
            analyticsContext,
            rowAccessibleContext: rowAccessibleContext,
            accessibilityStore,
            translationStore,
            iconFilename = 'InformationIcon',
            ...props
        } = this.props;

        const { metricContext } = analyticsContext;

        const { hasContent } = this;

        if (hasContent) {
            return (
                <TooltipWrapper {...props}>
                    <TooltipIconButton
                        aria-label={(() => {
                            const prefix =
                                translate(
                                    translationStore,
                                    accessibilityContext?.key,
                                    accessibilityContext?.placeholders
                                ) || 'More information about';
                            const rt = (this.props.rowText || '').trim();
                            return rt ? `${prefix} ${rt}` : prefix;
                        })()}
                        aria-describedby={this.show ? this.popupId : undefined}
                        ref={this.setAnchorNodeRef}
                        onClick={showContent ? this.toggleTooltip : () => null}
                        data-metric-id={`tooltip-${metricContext}`}
                        cursor={showContent ? 'pointer' : 'default'}
                        aria-expanded={this.show}
                        {...theme.TooltipIconButton}
                        {...this.keyboardEvents.onKeyProps}
                    >
                        {tooltipCharacter ? (
                            <TooltipCharacter {...theme.TooltipCharacter}>
                                {tooltipCharacter}
                            </TooltipCharacter>
                        ) : (
                            <TooltipIcon
                                fileName={iconFilename}
                                disableAltText={true}
                                {...theme.TooltipIcon}
                            />
                        )}
                    </TooltipIconButton>
                    {showContent && (
                        <Fragment>
                            {this.show && (
                                <ClickLayer
                                    data-metric-id={'tooltip-clickLayer'}
                                    onClick={this.toggleTooltip}
                                />
                            )}
                            {this.tooltipContent}
                        </Fragment>
                    )}
                </TooltipWrapper>
            );
        }

        return null;
    }
}

export default withTheme(Tooltip);



// WEBPACK FOOTER //
// ./src/Components/Tooltip.tsx